'use client';

import { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';

import MasterFuelPriceAddEdit from './MasterFuelPriceAddEdit';
import {
  useMasterFuelPriceList,
  deleteMasterFuelPrice,
  revalidateMasterFuelPriceList,
  fetchAllMasterFuelPrice
} from '../../../stores/masterFuelPriceStore';
import { useCompanyList } from '../../../stores/companyStore';

const MasterFuelPricePage = () => {
  const { t } = useLanguage();
  const notify = useNotifier();

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  // Search options follow the columns shown in the table (keys mapped in masterFuelPriceStore)
  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'company_name', label: t('general.company', 'Company') },
      { value: 'fuel_type_name', label: t('masterUser.name', 'Name') },
      { value: 'fuel_type_code', label: t('masterUser.code', 'Code') },
      { value: 'fuel_type_category', label: t('general.category', 'Category') },
      { value: 'fuel_type_uom', label: t('general.uomType', 'UOM Type') },
      { value: 'description', label: t('general.description', 'Description') },
      { value: 'price', label: 'Price (IDR)' },
      { value: 'valid_at', label: 'Valid At' }
    ],
    [t]
  );

  const { companyOptions } = useCompanyList();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('eror');
    }
  };

  // --- DEBOUNCE SEARCH INPUT ---
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = String(searchRaw ?? '').trim();
      setSearchValue((prev) => (prev === next ? prev : next));
      // keep same object when already on page 0 so it does not trigger an extra request
      setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(timer);
  }, [searchRaw]);

  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // single source for column search and 'all' search
  const {
    data: rows,
    isLoading,
    totalRecord: totalRecords
  } = useMasterFuelPriceList(paginationModel.page, paginationModel.pageSize, searchField, searchValue, filterCompanyIds);

  const handleFetchAllForExport = async () => {
    try {
      return await fetchAllMasterFuelPrice({ searchKey: searchField, searchValue, companyId: filterCompanyIds, total: totalRecords });
    } catch (err) {
      console.error('export all error', err);
      return [];
    }
  };

  const handleAdd = () => {
    setEditId(null);
    setAddEditOpen(true);
  };

  const handleEdit = (id) => {
    setEditId(id);
    setAddEditOpen(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete?.id) return;
    setIsDeleting(true);
    try {
      await deleteMasterFuelPrice(itemToDelete.id);
      notify({ message: t('notify.delete', 'Deleted successfully'), severity: 'success' });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      revalidateMasterFuelPriceList();
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error('Failed to delete:', err);
      const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Failed to delete data';
      notify({ message: errorMessage, severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDialogSuccess = () => {
    revalidateMasterFuelPriceList();
    setPaginationModel((p) => ({ ...p }));
  };

  const columns = useMemo(
    () => [
      {
        field: 'company_name',
        headerName: t('general.company', 'Company'),
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => row.company_name || row.company || row?.join?.company_name || '-'
      },
      {
        field: 'fuel_type_name',
        headerName: t('masterUser.name', 'Name'),
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => row.fuel_type_name || row?.fuel_type_join?.name || row.name || '-'
      },
      {
        field: 'fuel_type_code',
        headerName: t('masterUser.code', 'Code'),
        flex: 1,
        minWidth: 100,
        valueGetter: (value, row) => row.fuel_type_code || row?.fuel_type_join?.code || row.code || '-'
      },
      {
        field: 'fuel_type_category',
        headerName: t('general.category', 'Category'),
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => row.fuel_type_category || row?.fuel_type_join?.category || row.category || '-'
      },
      {
        field: 'fuel_type_uom',
        headerName: t('general.uomType', 'UOM Type'),
        flex: 1,
        minWidth: 130,
        valueGetter: (value, row) => row.fuel_type_uom || row?.fuel_type_join?.uom_type || row.uom_type || '-'
      },
      {
        field: 'description',
        headerName: t('general.description', 'Description'),
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => row.description || '-'
      },
      {
        field: 'price',
        headerName: 'Price (IDR)',
        flex: 1,
        minWidth: 130,
        valueGetter: (value, row) => {
          const pr = row.price;
          if (pr === undefined || pr === null) return '-';
          return 'Rp ' + Number(pr).toLocaleString('id-ID');
        }
      },
      {
        field: 'valid_at',
        headerName: 'Valid At',
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => {
          const val = row.valid_at;
          if (!val || String(val).startsWith('0001-01-01') || String(val).startsWith('1900-01-01')) return '-';
          return dayjs(val).isValid() ? dayjs(val).format('DD MMM YYYY') : '-';
        }
      },
      {
        field: 'id',
        headerName: 'Fuel Price ID',
        flex: 1,
        minWidth: 110,
        valueGetter: (value, row) => row.id ?? '-'
      },
      {
        field: 'fuel_type_id',
        headerName: 'Fuel Type ID',
        flex: 1,
        minWidth: 110,
        valueGetter: (value, row) =>
          row.fuel_type_id ??
          row.fuel_type ??
          row?.fuel_type_join?.id ??
          (typeof row.fuel_type === 'object' ? row.fuel_type?.id : null) ??
          '-'
      },
      {
        field: 'action',
        headerName: t('buttonTitle.action', 'Action'),
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <ActionMenu mode="inline" row={params.row} onEdit={handleEdit} onDelete={() => handleDeleteClick(params.row)} />
        )
      }
    ],
    [t]
  );

  return (
    <Box>
      <DataTable
        title="Fuel Price List"
        columns={columns}
        rows={rows}
        loading={isLoading}
        pagination
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          // page reset + searchValue update are handled by the debounce effect
          if (field && field !== searchField) setSearchField(field);
          setSearchRaw(value ?? '');
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f || 'all');
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
        }}
        searchLabel="Search here"
        onAddClick={handleAdd}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <MasterFuelPriceAddEdit open={addEditOpen} id={editId} onClose={() => setAddEditOpen(false)} onSuccess={handleDialogSuccess} />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        title={t('general.deleteConfirmTitle', 'Delete Confirmation')}
        content={t('general.deleteConfirmMessage', 'Are you sure you want to delete this record?')}
      />
    </Box>
  );
};

export default MasterFuelPricePage;
